import { useUser } from '@clerk/react-router';
import { useSelector } from '@xstate/react';
import { useEffect, useRef } from 'react';
import type { ActorRefFrom } from 'xstate';
import type { providerRegistrationView } from './registration-view.machine';

/**
 * [MCV-1762] Copies the name the provider typed on the "Provider Category" step onto
 * their Clerk user.
 *
 * The provider sign-up card no longer asks for a name (it is asked again, and required,
 * on that step), so an email/password provider reaches registration with an empty Clerk
 * firstName/lastName. createProvider writes the name to the provider record only, while
 * account settings, the avatar initial and the public header read the Clerk name.
 *
 * The trigger is the view machine's `providerId`, which is assigned only by
 * ON_PROVIDER_CREATED, i.e. after createProvider succeeded; a failed create never sets it.
 * It runs once: the ref is set the first time the Clerk user is available after that, and
 * nothing is set in React state afterwards. The update is started at provider creation,
 * well before the later user-role call and the completion redirect, and a client-side
 * navigation does not cancel it.
 *
 * A Clerk name that already exists (an OAuth name, or one the user set themselves) is never
 * overwritten, and a failed update is reported but never blocks registration.
 */
export const useSyncClerkName = (actorRef: ActorRefFrom<typeof providerRegistrationView>) => {
  const { isLoaded, user } = useUser();
  const providerId = useSelector(actorRef, (snapshot) => snapshot.context.providerId);
  const firstName = useSelector(
    actorRef,
    (snapshot) => snapshot.context.typeAndCategoryForm.firstName
  );
  const lastName = useSelector(
    actorRef,
    (snapshot) => snapshot.context.typeAndCategoryForm.lastName
  );
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current || !providerId || !isLoaded || !user) return;
    const first = (firstName ?? '').trim();
    const last = (lastName ?? '').trim();
    if (!first && !last) return;

    // Decided once, from here on: either the name is copied or Clerk already has one.
    handledRef.current = true;
    if (user.firstName?.trim() || user.lastName?.trim()) return;

    void (async () => {
      try {
        await user.update({ firstName: first, lastName: last });
      } catch (error) {
        console.error('[SERVICE] Failed to copy the registration name to the Clerk user:', error);
      }
    })();
  }, [providerId, isLoaded, user, firstName, lastName]);
};
